import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Chip, Menu, MenuItem, ListItemText, Radio } from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import { getTheme, THEME_VARIANTS } from './theme';
import { getDisplayVariant, setThemePreference } from './abtest';
import MatrixRain from './MatrixRain';

// Human-readable labels for the theme variants.
export const THEME_LABELS: Record<string, string> = {
  matrix: 'Matrix',
  clean: 'Clean',
  corporate: 'Corporate',
  warm: 'Warm',
};

interface ThemeSwitch {
  variant: string;
  setVariant: (v: string) => void;
}

const ThemeSwitchContext = createContext<ThemeSwitch>({
  variant: 'matrix',
  setVariant: () => {},
});

export const useThemeSwitch = () => useContext(ThemeSwitchContext);

/**
 * Owns the live, switchable theme. Applies the displayed variant (the user's
 * manual choice, or their assigned A/B bucket), keeps <html data-theme> + body
 * background in sync so index.css's matrix-only effects scope correctly, and
 * renders the Matrix rain canvas only for the matrix variant. Changing the
 * theme here only updates the *display* preference — never the A/B bucket sent
 * to the funnel (see abtest.ts).
 */
const ThemeController: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [variant, setVariantState] = useState<string>(() => getDisplayVariant());

  const setVariant = (v: string) => setVariantState(setThemePreference(v));

  const theme = useMemo(() => getTheme(variant), [variant]);

  useEffect(() => {
    document.documentElement.dataset.theme = variant;
    document.body.style.backgroundColor = theme.palette.background.default;
  }, [variant, theme]);

  const ctx = useMemo(() => ({ variant, setVariant }), [variant]);

  return (
    <ThemeSwitchContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {variant === 'matrix' && <MatrixRain />}
        {children}
      </ThemeProvider>
    </ThemeSwitchContext.Provider>
  );
};

/** Header control that lets a visitor switch the theme (display-only). */
export const ThemeSwitcher: React.FC = () => {
  const { variant, setVariant } = useThemeSwitch();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  return (
    <>
      <Chip
        size="small"
        clickable
        variant="outlined"
        color="primary"
        icon={<PaletteIcon />}
        label={`Theme: ${THEME_LABELS[variant] || variant}`}
        title="Change the look of the app"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ '& .MuiChip-icon': { color: 'inherit' } }}
      />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {THEME_VARIANTS.map((v) => (
          <MenuItem
            key={v}
            selected={v === variant}
            onClick={() => {
              setVariant(v);
              setAnchor(null);
            }}
          >
            <Radio checked={v === variant} size="small" sx={{ mr: 1, p: 0 }} />
            <ListItemText primary={THEME_LABELS[v] || v} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ThemeController;
